"use client";
import {
  type FC,
  type FormEvent,
  useEffect,
  useState,
  ChangeEvent,
} from "react";
import { CtaPrimary, CtaSecondary, RichText } from "../..";
import { ListElement } from "./ListElement";
import classNames from "classnames";
import { useToggle, useUpdateEffect } from "react-use";
import { ModalMintNfts } from "../../modals";
import { useWeb3ModalGuard } from "../../modals/shared";
import { useCustomWallet } from "../../providers";
import useAlertNotification from "../../hooks/useAlertNotification";
import { useLockeysHolderType } from "../../hooks/useLockeysHolderType";
import { handleErrors } from "../../../errors";
import getNameFromCollectionAddress from "../../../logic/helpers/nfts/getNameFromCollectionAddress";
import { Address } from "viem";
import faucetModule from "../../../logic/FaucetModule";
import { BasicProps } from "../../types/BasicProps";
import { Hash } from "@unlockdfinance/verislabs-web3";
import { verisModule } from "clients/verisModule";
import { app } from "app.config";

const FAUCET_LINKS_ETH = [
  {
    href: "https://sepoliafaucet.com/",
    label: "https://sepoliafaucet.com/",
  },
  {
    href: "https://www.infura.io/faucet/sepolia",
    label: "https://www.infura.io/faucet/sepolia",
  },
];

const FAUCET_LINKS_USDC = [
  {
    href: "https://staging.aave.com/faucet/",
    label: "https://staging.aave.com/faucet/",
  },
];

type Props = BasicProps & {
  onNewTransaction: (txHash: Hash) => void;
};

const FAUCET_ADDRESSES = app.FAUCET_COLLECTIONS.map(({ address }) => address);

export const FaucetForm: FC<Props> = (props) => {
  const { className, onNewTransaction } = props;
  const [isModalMintOpen, toggleModalMint] = useToggle(false);
  const { address } = useCustomWallet();
  const [sensor, toggle] = useState<boolean>(false);
  const [, openAlertNotification] = useAlertNotification();
  const [availableNfts, setAvailableNfts] = useState<number>();
  const [hasAvailableNftsToMint, setHasAvailableNftsToMint] =
    useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [collectionSelected, setCollectionSelected] = useState<Address>(
    FAUCET_ADDRESSES[0]
  );
  const lockeysHolderType = useLockeysHolderType();

  useEffect(() => {
    error && openAlertNotification("error", "unexpected error", 5000);
  }, [error]);

  useEffect(() => {
    address && handleGetAvailableNfts(address);
  }, [address, collectionSelected]);

  useUpdateEffect(() => {
    address && handleGetAvailableNfts(address);
  }, [sensor]);

  const handleMinted = () => {
    toggle(!sensor);
  };

  const handleGetAvailableNfts = async (address: Address) => {
    try {
      const {
        availableNfts: availableNftsResult,
        hasAvailableNftsToMint: hasAvailableNftsToMintResult,
      } = await faucetModule.getAvailableNftsForAddress(
        collectionSelected,
        address
      );

      setAvailableNfts(availableNftsResult);
      setHasAvailableNftsToMint(hasAvailableNftsToMintResult);
    } catch (err) {
      setError(handleErrors(err));
    }
  };

  const launcherModal = useWeb3ModalGuard(isModalMintOpen, toggleModalMint, {
    validators: [
      {
        validator: () => lockeysHolderType != undefined,
        notification: {
          type: "error",
          content: "Unexpected error",
          timeout: 10_000,
        },
      },
      {
        validator: () => !error,
        notification: {
          type: "error",
          content: "Unexpected error",
          timeout: 10_000,
        },
      },
      {
        validator: () => !!hasAvailableNftsToMint,
        notification: {
          type: "warning",
          content: "not available NFTs to Mint",
          timeout: 10_000,
        },
      },
      {
        validator: () => {
          const storedTransaction = verisModule.storage.local?.get(
            `account.${address}.transactionPending`,
            ""
          );

          return !storedTransaction.length;
        },
        notification: {
          type: "warning",
          content: "there is a pending transaction",
          timeout: 10_000,
        },
      },
    ],
  });

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCollectionSelected(event.target.value as Address);
  };

  const handleSendNft = (event: FormEvent) => {
    event.preventDefault();
    launcherModal();
  };

  return (
    <>
      <section
        className={classNames(
          "max-w-4xl mx-auto mb-5 rounded-t-xl border-unlockd p-0.5",
          [className]
        )}
      >
        <div className="w-full xs:px-6 px-4 pt-8 pb-8 rounded-t-xl bg-secondary">
          <form className="mb-6 flex flex-col gap-2" onSubmit={handleSendNft}>
            <div className="flex">
              <input
                type="text"
                className="w-full bg-secondary text-xs md:text-sm px-3 max-3xs:px-2 py-2 border
              rounded-full text-whiteplaceholder:text-white disabled:text-white mr-3"
                placeholder={
                  address
                    ? `${address}`
                    : "Connect your wallet and this field will be auto-completed"
                }
                disabled={true}
              />
              <CtaPrimary type="submit" className="w-40">
                Send me NFTs
              </CtaPrimary>
            </div>
            <div className="mt-2 mx-7 flex flex-col gap-2">
              <h2>Select the collection to mint: </h2>
              {FAUCET_ADDRESSES.map((address) => (
                <div className="flex items-center cursor-pointer" key={address}>
                  <input
                    checked={collectionSelected === address}
                    id={address}
                    type="radio"
                    onChange={handleRadioChange}
                    value={address}
                    name="collection-selected"
                    className="w-4 h-4 text-primary border border-white rounded-full checked:bg-primary cursor-pointer"
                  />
                  <label
                    htmlFor={address}
                    className="ml-2 text-sm font-medium cursor-pointer"
                  >
                    {getNameFromCollectionAddress(address)}
                  </label>
                </div>
              ))}
            </div>
          </form>

          <div className="mb-6 mx-7 text-sm">
            <span>
              To ensure you have the necessary funds to explore Unlockd on the
              Sepolia testnet, please obtain ETH and USDC through the following
              faucets:
            </span>
          </div>
          <span className="mx-7 text-sm">For ETH: </span>
          <ul className="list-disc list-inside mb-6 font-semibold ml-9">
            {FAUCET_LINKS_ETH.map(({ href, label }) => (
              <ListElement key={href} href={href} target="_blank">
                {label}
              </ListElement>
            ))}
          </ul>
          <span className="mx-7 text-sm">For USDC: </span>
          <ul className="list-disc list-inside mb-6 font-semibold ml-9">
            {FAUCET_LINKS_USDC.map(({ href, label }) => (
              <div className="flex gap-1 items-center">
                <ListElement key={href} href={href} target="_blank">
                  {label}
                </ListElement>
                <span className="font-normal">(Select USDC from the menu)</span>
              </div>
            ))}
          </ul>
          <div className="mb-11 mx-7 text-sm">
            <span>
              These resources provide you with the testnet tokens required to
              fully experience and test the functionalities of Unlockd.
            </span>
          </div>
          <div className="mb-3 mx-7 text-sm">
            <RichText value="Click on the button to copy the test nfts contract to the clipboard:" />
          </div>
          <div className="flex flex-col gap-3">
            {FAUCET_ADDRESSES.map((address) => (
              <CtaSecondary
                key={address}
                type="button"
                className="mx-7 w-fit px-10"
                onClick={() => {
                  navigator.clipboard.writeText(address);
                  openAlertNotification("success", "copy nft address", 5000);
                }}
              >
                {`Copy ${getNameFromCollectionAddress(address)} address`}
              </CtaSecondary>
            ))}
          </div>
        </div>
      </section>

      <ModalMintNfts
        collection={collectionSelected}
        onNewTransaction={onNewTransaction}
        isOpen={isModalMintOpen}
        toggleModal={toggleModalMint}
        availableNfts={availableNfts!}
        onMinted={handleMinted}
      />
    </>
  );
};
